import { Injectable, OnDestroy } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Site24Monitor } from './monitor.definitions';
import { WebsocketService } from '../../shared/websocket/websocket.service';
import { HttpClient } from '@angular/common/http';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { AbstractDataService } from '../../shared/abstract-data.service';
import { WebsocketMessage } from '~app/shared/websocket/websocketMessage';

@Injectable()
export class MonitorService extends AbstractDataService<Site24Monitor> implements OnDestroy {

  private readonly ngUnsubscribe = new Subject<void>();
  private readonly monitors = new ReplaySubject<Site24Monitor>(1);

  //// this is the 'api' for monitor data
  public readonly monitors$ = this.monitors.asObservable();

  constructor(http: HttpClient,
    private readonly websocket: WebsocketService,
    private readonly authService: AuthService,
    private readonly appMessageService: AppMessageService,
    private readonly translateService: TranslateService) {
    super(http, environment.dataApiURL + 'monitors/?');

    // Initialize after authentication
    authService.login$.pipe(take(1)).subscribe(() => {
      this.init();
    });
  }

  private async init() {
    console.log('Monitor service constructing');
    this.websocket.newWebsocketMessage$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.handleNewWebsocketMessage(data);
      });
    await this.initMonitorData();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async initMonitorData() {
    console.log('Init monitor data');
    const site24Monitor = (await this.listMonitors()).data[0];
    this.monitors.next(site24Monitor);
  }

  /**
   * fetch monitors from data-backend (should return single document)
   */
  private async listMonitors(): Promise<{ data: Site24Monitor, total: number }> {
    return this.http.get<{ data: Site24Monitor, total: number }>(`${environment.dataApiURL}monitors/?sort=-updatedAt`).toPromise();
  }

  /**
   * process update message against monitors
   * @param msg
   */
  private handleNewWebsocketMessage(msg: WebsocketMessage<any>) {
    if (msg.target.includes('monitors') && msg.body > 0) {
      this.initMonitorData();
    }
  }
}
